import { shallowRef, type AppConfig, type ComponentPublicInstance } from 'vue'

interface ApmErrorCapture {
    captureError: (error: unknown, options?: { labels?: Record<string, string | number | boolean> }) => void
}

type VueErrorArgs = Parameters<NonNullable<AppConfig['errorHandler']>>

const componentName = (instance: ComponentPublicInstance): string => {
    if (instance.$root === instance) return 'Root'

    return instance.$options.name || instance.$options.__name || 'Anonymous'
}

/** The error fields land in the error's custom context. A frozen thrown value is reported without them. */
export const captureVueError = (apm: ApmErrorCapture, [error, instance, info]: VueErrorArgs, labels?: Record<string, string | number | boolean>) => {
    if (error && typeof error === 'object' && Object.isExtensible(error)) {
        Object.assign(error, {
            ...(instance && { component: componentName(instance), file: instance.$options.__file || '' }),
            ...(info && { lifecycleHook: info }),
        })
    }

    apm.captureError(error, labels && { labels })
}

/**
 * The agent's Vue error handler, naming `<script setup>` components: apm-rum-vue's own reads only `options.name`,
 * which those never set, so it reports them all as "Anonymous".
 */
export const createApmVueErrorHandler = (apm: ApmErrorCapture, next?: AppConfig['errorHandler']): NonNullable<AppConfig['errorHandler']> =>
    (error, instance, info) => {
        captureVueError(apm, [error, instance, info])
        next?.(error, instance, info)
        // An app error handler stops Vue from logging the error itself.
        console.error(error)
    }

// Bounded, for a page that never installs the agent.
const EARLY_ERROR_LIMIT = 20

/**
 * Holds the Vue errors from before the agent is installed (boot, hydration, the first idle callback), which no handler
 * reports, so they can be captured once it is. `flush` stops holding, because the installed handler reports the rest.
 */
export const createEarlyVueErrors = () => {
    const held = shallowRef<Array<{ args: VueErrorArgs, at: number }> | null>([])

    return {
        hold: (...args: VueErrorArgs) => {
            if (!held.value || held.value.length >= EARLY_ERROR_LIMIT) return

            held.value.push({ args, at: performance.now() })
        },
        flush: (capture?: (args: VueErrorArgs, at: number) => void) => {
            const errors = held.value ?? []

            held.value = null
            errors.forEach(({ args, at }) => capture?.(args, at))
        },
    }
}
